import type { PlacedThought, ThoughtAnchor } from "../types/adventure";

export interface PositionPoint {
  x: number;
  y: number;
  anchor?: ThoughtAnchor;
  label?: string;
}

export interface PositionRect {
  left: number;
  top: number;
  width: number;
  height: number;
  label: string;
}

/** Collision footprint (packing density). */
const COLLISION_WIDTH_PCT = 16;
const COLLISION_HEIGHT_PCT = 8;
/** Viewport footprint, keeps bubbles inside the cockpit wrap. */
const VIEWPORT_WIDTH_PCT = 20;
const VIEWPORT_HEIGHT_PCT = 14;
const OVERLAY_HEIGHT_PCT = 10;
const HALF_COLLISION_WIDTH = COLLISION_WIDTH_PCT / 2;
const HALF_VIEWPORT_WIDTH = VIEWPORT_WIDTH_PCT / 2;

const ORGANIC_X_SPREAD = 5;
const ORGANIC_Y_SPREAD = 0;
const OFFSET_ATTEMPTS = 6;

/** Cockpit console where choice buttons live (`.act-cockpit__console`). */
export const CONSOLE_ZONE: PositionRect = {
  left: 6,
  top: 78,
  width: 88,
  height: 16,
  label: "Console / knoppen",
};

const NORMAL_MAX_BOTTOM = CONSOLE_ZONE.top;
const OVERLAY_MAX_BOTTOM = CONSOLE_ZONE.top + CONSOLE_ZONE.height;

/**
 * Rectangular bands where thought bubbles may be placed randomly.
 * Center-anchor: x/y are bubble center points within each band.
 */
const BELOW_BAND_TOP = 36;
const BELOW_BAND_HEIGHT = CONSOLE_ZONE.top - BELOW_BAND_TOP - VIEWPORT_HEIGHT_PCT;

export const PLACEMENT_BANDS: PositionRect[] = [
  { left: 4, top: 3, width: 22, height: 31, label: "Links flank" },
  { left: 74, top: 3, width: 22, height: 31, label: "Rechts flank" },
  { left: 4, top: BELOW_BAND_TOP, width: 24, height: BELOW_BAND_HEIGHT, label: "Links onder" },
  { left: 32, top: BELOW_BAND_TOP, width: 36, height: BELOW_BAND_HEIGHT, label: "Midden onder" },
  { left: 72, top: BELOW_BAND_TOP, width: 24, height: BELOW_BAND_HEIGHT, label: "Rechts onder" },
];

/**
 * Narrow centre band where challenge text appears, thoughts avoid this.
 * Matches `.act-cockpit__alert` (left/right: 30%).
 */
export const CHALLENGE_EXCLUSION: PositionRect = {
  left: 30,
  top: 9,
  width: 40,
  height: 26,
  label: "Uitdaging-tekst (niet veilig)",
};

/**
 * Positions that spread across the full width of the cockpit console.
 * Used by overlay thoughts in round 5 to completely hide the buttons.
 */
export const OVERLAY_ZONES: PositionPoint[] = [
  { x: 18, y: 80, label: "Overlay 1" },
  { x: 38, y: 82, label: "Overlay 2" },
  { x: 58, y: 80, label: "Overlay 3" },
  { x: 78, y: 82, label: "Overlay 4" },
  { x: 48, y: 81, label: "Overlay 5" },
];

export const POSITION_DEBUG = {
  placementBands: PLACEMENT_BANDS,
  overlayZones: OVERLAY_ZONES,
  challengeExclusion: CHALLENGE_EXCLUSION,
  consoleZone: CONSOLE_ZONE,
} as const;

function seeded(seed: number): number {
  const v = Math.sin(seed * 127.1 + 311.7) * 43758.5453;
  return v - Math.floor(v);
}

function shuffleIndices(length: number, seed: number): number[] {
  const indices = Array.from({ length }, (_, i) => i);
  for (let i = length - 1; i > 0; i--) {
    const j = Math.floor(seeded(seed + i) * (i + 1));
    [indices[i], indices[j]] = [indices[j], indices[i]];
  }
  return indices;
}

function floatOffset(seed: number, range: number): number {
  return Math.round((seeded(seed) * 2 - 1) * range);
}

function floatParams(seed: number): PlacedThought["float"] {
  return {
    x1: floatOffset(seed + 1, 10),
    y1: floatOffset(seed + 2, 8),
    x2: floatOffset(seed + 3, 14),
    y2: floatOffset(seed + 4, 10),
    x3: floatOffset(seed + 5, 8),
    y3: floatOffset(seed + 6, 12),
    duration: 10 + Math.round(seeded(seed + 7) * 8),
    delay: Math.round(seeded(seed + 8) * 40) / 10,
    spawnX: floatOffset(seed + 9, 20),
    spawnY: floatOffset(seed + 10, 20),
  };
}

export function anchorForX(x: number): ThoughtAnchor {
  if (x <= VIEWPORT_WIDTH_PCT) return "left";
  if (x >= 100 - VIEWPORT_WIDTH_PCT) return "right";
  return "default";
}

function bandXRange(band: PositionRect): { minX: number; maxX: number } {
  return {
    minX: band.left + HALF_COLLISION_WIDTH,
    maxX: band.left + band.width - HALF_COLLISION_WIDTH,
  };
}

function bandMaxY(band: PositionRect, heightPct: number): number {
  return Math.min(
    band.top + band.height - heightPct,
    NORMAL_MAX_BOTTOM - VIEWPORT_HEIGHT_PCT
  );
}

function randomPointInBand(band: PositionRect, seed: number): { x: number; y: number } {
  const { minX, maxX } = bandXRange(band);
  const minY = band.top;
  const maxY = bandMaxY(band, COLLISION_HEIGHT_PCT);
  const xSpan = maxX - minX;

  return {
    x: xSpan <= 0 ? band.left + band.width / 2 : minX + seeded(seed) * xSpan,
    y: minY + seeded(seed + 1) * Math.max(0, maxY - minY),
  };
}

function xPointsInBand(band: PositionRect): number[] {
  const { minX, maxX } = bandXRange(band);
  const xSpan = maxX - minX;

  if (xSpan <= 0) return [band.left + band.width / 2];
  if (xSpan < COLLISION_WIDTH_PCT) return [(minX + maxX) / 2];

  const points: number[] = [];
  for (let x = minX; x <= maxX + 0.01; x += COLLISION_WIDTH_PCT) {
    points.push(x);
  }
  return points;
}

function buildCandidateCells(): { x: number; y: number }[] {
  const cells: { x: number; y: number }[] = [];

  for (const band of PLACEMENT_BANDS) {
    const maxY = bandMaxY(band, COLLISION_HEIGHT_PCT);

    for (let y = band.top; y <= maxY; y += COLLISION_HEIGHT_PCT) {
      for (const x of xPointsInBand(band)) {
        cells.push({ x, y });
      }
    }
  }

  return cells;
}

const CANDIDATE_CELLS = buildCandidateCells();

function organicOffset(seed: number): { x: number; y: number } {
  return {
    x: (seeded(seed) * 2 - 1) * ORGANIC_X_SPREAD,
    y: (seeded(seed + 1) * 2 - 1) * ORGANIC_Y_SPREAD,
  };
}

export function estimateThoughtBounds(
  x: number,
  y: number,
  anchor: ThoughtAnchor,
  widthPct = VIEWPORT_WIDTH_PCT,
  heightPct = VIEWPORT_HEIGHT_PCT
): PositionRect {
  let left: number;

  if (anchor === "center") {
    left = x - widthPct / 2;
  } else if (anchor === "left") {
    left = x - widthPct;
  } else {
    left = x;
  }

  return {
    left,
    top: y,
    width: widthPct,
    height: heightPct,
    label: "",
  };
}

function collisionBounds(
  x: number,
  y: number,
  anchor: ThoughtAnchor,
  widthPct = COLLISION_WIDTH_PCT,
  heightPct = COLLISION_HEIGHT_PCT
): PositionRect {
  return estimateThoughtBounds(x, y, anchor, widthPct, heightPct);
}

function thoughtBounds(
  thought: Pick<PlacedThought, "x" | "y" | "anchor"> & { overlay?: boolean },
  widthPct = COLLISION_WIDTH_PCT,
  heightPct?: number
): PositionRect {
  const h = heightPct ?? (thought.overlay ? OVERLAY_HEIGHT_PCT : COLLISION_HEIGHT_PCT);
  return collisionBounds(thought.x, thought.y, thought.anchor, widthPct, h);
}

function pickOverlayPlacement(
  zoneIndex: number,
  seed: number
): { x: number; y: number; anchor: ThoughtAnchor } {
  const anchor: ThoughtAnchor = "center";
  const zone = OVERLAY_ZONES[zoneIndex % OVERLAY_ZONES.length];
  const jitter = (s: number) => Math.round(((s * 17) % 5) - 2);

  for (let attempt = 0; attempt < 12; attempt++) {
    const x = zone.x + jitter(seed + attempt * 3);
    const y = zone.y + jitter(seed + attempt * 3 + 1);
    if (fitsViewport(x, y, anchor, OVERLAY_HEIGHT_PCT, OVERLAY_MAX_BOTTOM)) {
      return { x, y, anchor };
    }
  }

  const minX = CONSOLE_ZONE.left + HALF_VIEWPORT_WIDTH;
  const maxX = CONSOLE_ZONE.left + CONSOLE_ZONE.width - HALF_VIEWPORT_WIDTH;
  const maxY = OVERLAY_MAX_BOTTOM - OVERLAY_HEIGHT_PCT;

  for (let y = CONSOLE_ZONE.top; y <= maxY; y += 2) {
    for (let x = minX; x <= maxX; x += 8) {
      if (fitsViewport(x, y, anchor, OVERLAY_HEIGHT_PCT, OVERLAY_MAX_BOTTOM)) {
        return { x, y, anchor };
      }
    }
  }

  return { x: zone.x, y: CONSOLE_ZONE.top, anchor };
}

function overlapsRect(a: PositionRect, b: PositionRect): boolean {
  return (
    a.left < b.left + b.width &&
    a.left + a.width > b.left &&
    a.top < b.top + b.height &&
    a.top + a.height > b.top
  );
}

function fitsViewport(
  x: number,
  y: number,
  anchor: ThoughtAnchor,
  heightPct = VIEWPORT_HEIGHT_PCT,
  maxBottom = NORMAL_MAX_BOTTOM
): boolean {
  if (y < 0 || y + heightPct > maxBottom) return false;

  if (anchor === "center") {
    return x - HALF_VIEWPORT_WIDTH >= 0 && x + HALF_VIEWPORT_WIDTH <= 100;
  }
  if (anchor === "left") return x - VIEWPORT_WIDTH_PCT >= 0;
  return x + VIEWPORT_WIDTH_PCT <= 100;
}

interface PlacementRules {
  collisionWidth: number;
  collisionHeight: number;
  checkBubbleCollision: boolean;
  checkExclusion: boolean;
}

const STRICT_RULES: PlacementRules = {
  collisionWidth: COLLISION_WIDTH_PCT,
  collisionHeight: COLLISION_HEIGHT_PCT,
  checkBubbleCollision: true,
  checkExclusion: true,
};

const RELAXED_RULES: PlacementRules = {
  collisionWidth: COLLISION_WIDTH_PCT - 4,
  collisionHeight: COLLISION_HEIGHT_PCT - 2,
  checkBubbleCollision: true,
  checkExclusion: true,
};

const VIEWPORT_ONLY_RULES: PlacementRules = {
  collisionWidth: COLLISION_WIDTH_PCT - 4,
  collisionHeight: COLLISION_HEIGHT_PCT - 2,
  checkBubbleCollision: false,
  checkExclusion: true,
};

function isValidPlacement(
  x: number,
  y: number,
  anchor: ThoughtAnchor,
  occupied: Pick<PlacedThought, "x" | "y" | "anchor">[],
  rules: PlacementRules = STRICT_RULES
): boolean {
  if (!fitsViewport(x, y, anchor)) return false;

  const bounds = collisionBounds(x, y, anchor, rules.collisionWidth, rules.collisionHeight);
  if (rules.checkExclusion && overlapsRect(bounds, CHALLENGE_EXCLUSION)) return false;

  if (!rules.checkBubbleCollision) return true;

  return !occupied.some((t) =>
    overlapsRect(bounds, thoughtBounds(t, rules.collisionWidth, rules.collisionHeight))
  );
}

function findGridPlacement(
  occupied: Pick<PlacedThought, "x" | "y" | "anchor">[],
  rules: PlacementRules = STRICT_RULES
): { x: number; y: number; anchor: ThoughtAnchor } | null {
  const anchor: ThoughtAnchor = "center";

  for (const yStep of [rules.collisionHeight, 4, 2, 1]) {
    for (const xStep of [rules.collisionWidth, 4, 2, 1]) {
      for (const band of PLACEMENT_BANDS) {
        const { minX, maxX } = bandXRange(band);
        const maxY = bandMaxY(band, rules.collisionHeight);

        for (let y = band.top; y <= maxY; y += yStep) {
          for (let x = minX; x <= maxX; x += xStep) {
            if (isValidPlacement(x, y, anchor, occupied, rules)) {
              return { x, y, anchor };
            }
          }
        }
      }
    }
  }

  return null;
}

function pickRandomPlacement(
  slotIndex: number,
  seed: number,
  occupied: Pick<PlacedThought, "x" | "y" | "anchor">[],
  rules: PlacementRules = STRICT_RULES
): { x: number; y: number; anchor: ThoughtAnchor } | null {
  const anchor: ThoughtAnchor = "center";
  const cellOrder = shuffleIndices(CANDIDATE_CELLS.length, seed + slotIndex * 13);

  for (let c = 0; c < CANDIDATE_CELLS.length; c++) {
    const cell = CANDIDATE_CELLS[cellOrder[(slotIndex + c) % CANDIDATE_CELLS.length]];

    for (let attempt = 0; attempt < OFFSET_ATTEMPTS; attempt++) {
      const offset = organicOffset(seed + c * 11 + attempt * 5);
      const x = cell.x + offset.x;
      const y = cell.y + offset.y;
      if (isValidPlacement(x, y, anchor, occupied, rules)) {
        return { x, y, anchor };
      }
    }
  }

  for (let b = 0; b < PLACEMENT_BANDS.length; b++) {
    const band = PLACEMENT_BANDS[(slotIndex + b) % PLACEMENT_BANDS.length];
    for (let attempt = 0; attempt < 8; attempt++) {
      const point = randomPointInBand(band, seed + b * 19 + attempt * 7);
      if (isValidPlacement(point.x, point.y, anchor, occupied, rules)) {
        return { x: point.x, y: point.y, anchor };
      }
    }
  }

  return findGridPlacement(occupied, rules);
}

function resolveValidPlacement(
  slotIndex: number,
  seed: number,
  occupied: Pick<PlacedThought, "x" | "y" | "anchor">[]
): { x: number; y: number; anchor: ThoughtAnchor } {
  const anchor: ThoughtAnchor = "center";
  const ruleSets = [STRICT_RULES, RELAXED_RULES, VIEWPORT_ONLY_RULES];

  for (const rules of ruleSets) {
    const picked = pickRandomPlacement(slotIndex, seed, occupied, rules);
    if (picked) return picked;

    const grid = findGridPlacement(occupied, rules);
    if (grid) return grid;

    for (const cell of CANDIDATE_CELLS) {
      if (isValidPlacement(cell.x, cell.y, anchor, occupied, rules)) {
        return { ...cell, anchor };
      }
    }
  }

  for (const band of PLACEMENT_BANDS) {
    const { minX, maxX } = bandXRange(band);
    const y = band.top;
    const x = Math.min(maxX, Math.max(minX, band.left + band.width / 2));
    if (fitsViewport(x, y, anchor)) {
      return { x, y, anchor };
    }
  }

  return { x: 50, y: 3, anchor };
}

export function placementSeedFromId(id: string): number {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash * 31 + id.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

export function countSafeZoneSlots(thoughts: PlacedThought[]): number {
  return thoughts.filter((t) => !t.overlay).length;
}

export function placeThoughts(
  thoughts: {
    id: string;
    system: string;
    helpful: boolean;
    text: string;
    round: number;
    overlay?: boolean;
  }[],
  safeSlotsUsed: number,
  placementSeed: number,
  existingThoughts: PlacedThought[] = []
): PlacedThought[] {
  let overlayCount = 0;
  let safeSlot = safeSlotsUsed;
  const occupied: Pick<PlacedThought, "x" | "y" | "anchor">[] = [...existingThoughts];
  const placed: PlacedThought[] = [];

  for (let i = 0; i < thoughts.length; i++) {
    const t = thoughts[i];
    const seed = placementSeed + safeSlot * 31 + i * 17 + t.id.length;
    let x: number;
    let y: number;
    let anchor: ThoughtAnchor;

    if (t.overlay) {
      const picked = pickOverlayPlacement(overlayCount, seed);
      overlayCount++;
      x = picked.x;
      y = picked.y;
      anchor = picked.anchor;
    } else {
      const picked = resolveValidPlacement(safeSlot, seed, occupied);
      x = picked.x;
      y = picked.y;
      anchor = picked.anchor;
      safeSlot++;
    }

    const thought: PlacedThought = {
      thoughtId: t.id,
      system: t.system,
      helpful: t.helpful,
      text: t.text,
      round: t.round,
      overlay: !!t.overlay,
      x,
      y,
      anchor,
      float: floatParams(seed),
    };

    placed.push(thought);
    if (!t.overlay) {
      occupied.push(thought);
    }
  }

  return placed;
}

export function countBlockingThoughts(thoughts: PlacedThought[]): number {
  return thoughts.filter((t) => t.overlay).length;
}

export function buttonsAreBlocked(thoughts: PlacedThought[], phase: string): boolean {
  if (phase === "playing" && thoughts.some((t) => t.round === 5)) {
    return countBlockingThoughts(thoughts) > 0;
  }
  return false;
}
